/* eslint-disable no-nested-ternary */
/* eslint-disable react/no-array-index-key */
import { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import logo from '../../images/logoDark.png';
import { useAuth } from '../../utils/authContext';

function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [isMenuOpen, setMenuOpen] = useState(false);
  const [isMobile, setMobile] = useState(window.innerWidth < 768);
  const [hoveredItem, setHoveredItem] = useState(null);
  const { isLoggedIn, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.pageYOffset > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const toggleMenu = () => setMenuOpen(!isMenuOpen);

  let navItems;
  if (isLoggedIn) {
    navItems = [
      {
        label: 'Déconnexion',
        action: () => {
          logout();
          navigate('/login');
        }
      }
    ];
  }
  else {
    navItems = [
      { label: 'Qui sommes-nous', path: '/qui-sommes-nous' },
      { label: 'Actualités', path: '/actualites' },
      { label: 'Recrutement', path: '/recrutement' },
      { label: 'Contact', path: '/contact' },
      { label: 'Tarifs', path: '/tarifs' },
    ];
  }

  const baseHeaderStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    background: scrolled
      ? 'rgba(20, 30, 48, 0.85)'
      : 'rgba(15, 23, 42, 0.5)',
    backdropFilter: 'blur(15px)',
    boxShadow: scrolled
      ? '0 5px 30px rgba(0, 0, 0, 0.25)'
      : '0 2px 10px rgba(0, 0, 0, 0.1)',
    borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
    zIndex: 1000,
    padding: '15px 40px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    transition: 'all 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
    fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
  };

  const logoStyle = {
    height: 'auto',
    width: isMobile ? '150px' : '200px',
    transition: 'all 0.3s ease',
    filter: 'brightness(1.1)',
  };

  const navContainerStyle = {
    display: isMobile ? 'none' : 'flex',
    alignItems: 'center',
    gap: '14px',
    position: 'relative',
  };

  const navLinkBaseStyle = {
    color: '#fff',
    textDecoration: 'none',
    padding: '10px 20px',
    fontSize: '0.95rem',
    fontWeight: '600',
    position: 'relative',
    borderRadius: '12px',
    transition: 'all 0.3s ease',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    letterSpacing: '0.3px',
    textShadow: '0 1px 2px rgba(0, 0, 0, 0.15)',
  };

  const getNavLinkStyle = (path) => {
    const isActive = location.pathname === path;
    return {
      ...navLinkBaseStyle,
      background: isActive
        ? 'rgba(255, 255, 255, 0.1)'
        : 'transparent',
      color: isActive
        ? '#fff'
        : 'rgba(255, 255, 255, 0.85)',
      boxShadow: isActive
        ? '0 5px 15px rgba(0, 0, 0, 0.1)'
        : 'none',
    };
  };

  const mobileMenuTriggerStyle = {
    background: 'rgba(255, 255, 255, 0.05)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    borderRadius: '10px',
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    padding: '12px',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
    zIndex: 2000,
    backdropFilter: 'blur(5px)',
  };

  const hamburgerLineStyle = (index) => ({
    width: '24px',
    height: '2px',
    background: '#fff',
    borderRadius: '4px',
    transition: 'all 0.3s ease',
    transformOrigin: 'center',
    transform: isMenuOpen && index === 0
      ? 'rotate(45deg) translate(6px, 6px)'
      : isMenuOpen && index === 2
        ? 'rotate(-45deg) translate(6px, -6px)'
        : isMenuOpen && index === 1
          ? 'translateX(20px) opacity: 0'
          : 'none',
    opacity: isMenuOpen && index === 1 ? 0 : 1,
  });

  const mobileMenuStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100vw',
    height: '100vh',
    background: 'rgba(14, 25, 45, 0.95)',
    backdropFilter: 'blur(20px)',
    zIndex: 1500,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
  };

  const menuVariants = {
    hidden: { opacity: 0, y: -50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: [0.22, 1, 0.36, 1],
        staggerChildren: 0.1,
      }
    },
    exit: {
      opacity: 0,
      y: -50,
      transition: {
        duration: 0.3,
        ease: [0.22, 1, 0.36, 1],
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: [0.22, 1, 0.36, 1],
      }
    },
    exit: {
      opacity: 0,
      y: 20,
      transition: {
        duration: 0.2,
        ease: [0.22, 1, 0.36, 1],
      }
    }
  };

  return (
    <>
      <header style={baseHeaderStyle}>
        {/* Glow effect in the background */}
        <div style={{
          position: 'absolute',
          top: '-100px',
          left: '10%',
          width: '200px',
          height: '200px',
          background: 'rgba(58, 134, 255, 0.2)',
          filter: 'blur(80px)',
          borderRadius: '50%',
          zIndex: -1,
          opacity: 0.6,
        }}
        />
        <div style={{
          position: 'absolute',
          bottom: '-120px',
          right: '20%',
          width: '250px',
          height: '250px',
          background: 'rgba(255, 0, 110, 0.2)',
          filter: 'blur(90px)',
          borderRadius: '50%',
          zIndex: -1,
          opacity: 0.5,
        }}
        />

        <div style={{ flex: '0 0 auto' }}>
          <Link to="/">
            <motion.img
              src={logo}
              alt="Drivesoft Logo"
              style={logoStyle}
              whileHover={{ scale: 1.05 }}
              transition={{ duration: 0.3 }}
            />
          </Link>
        </div>

        <nav style={navContainerStyle}>
          {/* Animated blur background for the nav links */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            style={{
              position: 'absolute',
              top: '-15px',
              left: '-20px',
              right: '-20px',
              bottom: '-15px',
              background: 'rgba(20, 30, 48, 0.3)',
              backdropFilter: 'blur(8px)',
              borderRadius: '20px',
              zIndex: -1,
            }}
          />

          {navItems.map((item, index) => (
            <motion.div
              key={index}
              onHoverStart={() => setHoveredItem(index)}
              onHoverEnd={() => setHoveredItem(null)}
              whileHover={{ scale: 1.05 }}
              transition={{ duration: 0.3 }}
            >
              {item.path ? (
                <Link
                  style={getNavLinkStyle(item.path)}
                  to={item.path}
                >
                  {item.label}
                  {hoveredItem === index && (
                    <motion.div
                      layoutId="navHover"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'rgba(255, 255, 255, 0.1)',
                        borderRadius: '12px',
                        zIndex: -1,
                      }}
                    />
                  )}
                </Link>
              ) : (
                <button
                  type="button"
                  style={navLinkBaseStyle}
                  onClick={item.action}
                >
                  {item.label}
                  {hoveredItem === index && (
                    <motion.div
                      layoutId="navHover"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'rgba(255, 255, 255, 0.1)',
                        borderRadius: '12px',
                        zIndex: -1,
                      }}
                    />
                  )}
                </button>
              )}
            </motion.div>
          ))}
        </nav>

        {isMobile && (
          <button
            type="button"
            style={mobileMenuTriggerStyle}
            onClick={toggleMenu}
            aria-label={isMenuOpen ? 'Fermer le menu' : 'Ouvrir le menu'}
          >
            <div style={hamburgerLineStyle(0)} />
            <div style={hamburgerLineStyle(1)} />
            <div style={hamburgerLineStyle(2)} />
          </button>
        )}
      </header>

      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            style={mobileMenuStyle}
            variants={menuVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            {/* Background decoration for the mobile menu */}
            <div style={{
              position: 'absolute',
              width: '500px',
              height: '500px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, rgba(58, 134, 255, 0.1), rgba(131, 56, 236, 0.1))',
              filter: 'blur(80px)',
              top: '20%',
              left: '-200px',
              zIndex: 0,
              opacity: 0.7,
            }}
            />
            <div style={{
              position: 'absolute',
              width: '300px',
              height: '300px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, rgba(255, 0, 110, 0.1), rgba(131, 56, 236, 0.05))',
              filter: 'blur(60px)',
              bottom: '10%',
              right: '-100px',
              zIndex: 0,
              opacity: 0.6,
            }}
            />

            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '20px',
              alignItems: 'center',
              width: '100%',
              position: 'relative',
              zIndex: 5,
            }}
            >
              {navItems.map((item, index) => (
                <motion.div
                  key={index}
                  variants={itemVariants}
                  style={{ width: '100%', maxWidth: '300px' }}
                >
                  {item.path ? (
                    <Link
                      to={item.path}
                      onClick={() => setMenuOpen(false)}
                      style={{
                        textDecoration: 'none',
                        color: '#fff',
                        fontSize: '1.5rem',
                        fontWeight: '600',
                        padding: '15px',
                        borderRadius: '12px',
                        display: 'block',
                        textAlign: 'center',
                        background: location.pathname === item.path ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                        transition: 'all 0.3s ease',
                        backdropFilter: location.pathname === item.path ? 'blur(5px)' : 'none',
                        border: '1px solid rgba(255, 255, 255, 0.05)',
                      }}
                    >
                      {item.label}
                    </Link>
                  ) : (
                    <button
                      type="button"
                      style={{
                        background: 'transparent',
                        border: '1px solid rgba(255, 255, 255, 0.05)',
                        cursor: 'pointer',
                        color: '#fff',
                        fontSize: '1.5rem',
                        fontWeight: '600',
                        padding: '15px',
                        borderRadius: '12px',
                        width: '100%',
                        textAlign: 'center',
                      }}
                      onClick={() => {
                        setMenuOpen(false);
                        item.action();
                      }}
                    >
                      {item.label}
                    </button>
                  )}
                </motion.div>
              ))}

              {!isLoggedIn && (
                <motion.div
                  variants={itemVariants}
                  style={{
                    width: '100%',
                    maxWidth: '300px',
                    marginTop: '10px'
                  }}
                >
                  <Link
                    to="/register"
                    onClick={() => setMenuOpen(false)}
                    style={{
                      background: 'rgba(255, 255, 255, 0.1)',
                      color: '#fff',
                      fontSize: '1.25rem',
                      fontWeight: '600',
                      padding: '15px',
                      borderRadius: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '10px',
                      textDecoration: 'none',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      backdropFilter: 'blur(5px)',
                      position: 'relative',
                      overflow: 'hidden',
                    }}
                  >
                    <span style={{ position: 'relative', zIndex: 2 }}>S'inscrire</span>
                    {/* Subtle animation */}
                    <motion.div
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0))',
                        zIndex: 1,
                        opacity: 0,
                      }}
                      animate={{ opacity: [0, 0.2, 0] }}
                      transition={{ duration: 3, repeat: Infinity }}
                    />
                  </Link>
                </motion.div>
              )}
            </div>

            <motion.div
              variants={itemVariants}
              style={{
                position: 'absolute',
                bottom: '40px',
                color: 'rgba(255, 255, 255, 0.7)',
                fontSize: '0.9rem',
                fontWeight: '500',
                letterSpacing: '0.5px',
                textAlign: 'center',
                width: '100%',
                padding: '0 20px',
              }}
            >
              © 2025 Drivesoft - Tous droits réservés
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

export default Header;
